import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { DownloadOutlined, FileOutlined, PaperClipOutlined } from '@ant-design/icons';
import type { Artifact } from '../types';
import * as api from '../api/client';
import AddToKnowledgeBaseButton from './AddToKnowledgeBaseButton';

const { Text } = Typography;

interface Props {
  artifacts: Artifact[];
  userId?: string;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / (1024 * 1024)).toFixed(1)}MB`;
}

function mimeIcon(contentType: string): React.ReactNode {
  return <FileOutlined style={{ color: '#1890ff' }} />;
}

const ArtifactPanel: React.FC<Props> = ({ artifacts, userId = '' }) => {
  if (artifacts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0', color: '#555', fontSize: 12 }}>
        <PaperClipOutlined style={{ fontSize: 28, color: '#1e2736', marginBottom: 8, display: 'block' }} />
        No artifacts yet
      </div>
    );
  }

  return (
    <div>
      <Text
        style={{
          color: '#555', fontSize: 11, textTransform: 'uppercase',
          letterSpacing: 1, display: 'block', marginBottom: 10,
        }}
      >
        Artifacts ({artifacts.length})
      </Text>
      {artifacts.map(a => (
        <div
          key={a.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 10px',
            background: '#161b22',
            border: '1px solid #1e2736',
            borderRadius: 6,
            marginBottom: 6,
          }}
        >
          <Space size={8}>
            {mimeIcon(a.content_type)}
            <div>
              <Text style={{ color: '#e6edf3', fontSize: 12, display: 'block' }}>
                {a.filename}
              </Text>
              <Space size={6} style={{ marginTop: 2 }}>
                <Tag style={{ fontSize: 10, padding: '0 4px' }}>{a.content_type || 'unknown'}</Tag>
                <Text style={{ color: '#555', fontSize: 11 }}>{formatBytes(a.size_bytes)}</Text>
                <Text style={{ color: '#555', fontSize: 11 }}>
                  {new Date(a.created_at).toLocaleTimeString()}
                </Text>
              </Space>
            </div>
          </Space>
          <Space>
            <AddToKnowledgeBaseButton
              userId={userId}
              draft={{
                suggestedTitle: a.filename || 'Artifact',
                summary: `Artifact: ${a.filename}`,
                contentMarkdown: `Artifact **${a.filename}** (${a.content_type || 'unknown'})`,
                sourceModule: 'artifact',
                sourceId: a.id,
                tags: ['artifact'],
                artifactIds: [a.id],
                metadata: {
                  artifact_id: a.id,
                  content_type: a.content_type,
                  size_bytes: a.size_bytes,
                  created_at: a.created_at,
                },
              }}
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => void api.downloadArtifact(a.id, a.filename)}
              style={{ background: '#0d1117', borderColor: '#30363d', color: '#58a6ff' }}
            >
              Download
            </Button>
          </Space>
        </div>
      ))}
    </div>
  );
};

export default ArtifactPanel;
